import { useEffect, useState } from 'react'
import { getAllOdds } from 'src/actions/getAllOdds'
import { type OddsResult } from 'src/utils/calculationService'
import { useClientSideTranslation } from 'src/services/i18n'
import { useGetCurrentGame, useGetCurrentLanguage, useGetGameConfig } from 'src/store/game'
import { getSelectionTypeOptions } from 'src/utils/gameTypeUtils'
import { isExoticBetEnabled } from 'src/utils/isExoticBetEnabled'
import { truncateDecimals } from 'src/utils/truncateDecimals'
import { isKnownGameConfig } from 'src/types/domain/GameConfig'
import { isMarbleGame } from 'src/types/domain/Game'
import { getEffectiveBetStats } from 'src/utils/gameConfigStats'
import {
  effectiveRtp,
  hasLightning,
  resolveGameKey,
  sideBetMultiplier,
  sideBetRtp,
  type SideBetType
} from 'src/utils/effectiveRtp'

type Props = Readonly<{
  totalMarbles: number
  maxPickableMarbles: number
  gameType: string
  equalOdds: boolean
  // Chip-placement reshapes the Pick Winner column: the player can
  // only pick a single marble per main bet (multi-marble bets are
  // submitted as forecast/tricast through the dedicated Pick Order
  // tables below). Showing 2/3-marble PickWinner rows here gives a
  // false impression of the bet types available. Defaults to false
  // so classic-mode callers get the original three rows unchanged.
  isChipPlacement?: boolean
}>

export function FullRtpTable({
  totalMarbles,
  maxPickableMarbles,
  gameType,
  equalOdds,
  isChipPlacement = false
}: Props) {
  const { t } = useClientSideTranslation(useGetCurrentLanguage(), 'gameRules')
  const gameConfig = useGetGameConfig()
  const game = useGetCurrentGame()

  const [odds, setOdds] = useState<{
    singleMarble?: OddsResult
    twoMarble?: OddsResult
    threeMarble?: OddsResult
  }>({})

  const calculateOddsRtp = (odds: OddsResult | undefined) => {
    if (!odds) return null
    return 100 - ((odds.odds - odds.oddsAfterMargin) / odds.odds) * 100
  }

  const formatRTP = (rtp: number | null | undefined) => {
    if (rtp === null || rtp === undefined) return '...'
    return `${rtp.toFixed(2)}%`
  }

  useEffect(() => {
    const fetchOdds = async () => {
      const [singleMarbleOdds, twoMarbleOdds, threeMarbleOdds] = await Promise.all([
        getAllOdds({ totalItemsSelected: 1, totalItems: totalMarbles }),
        getAllOdds({ totalItemsSelected: 2, totalItems: totalMarbles }),
        getAllOdds({ totalItemsSelected: 3, totalItems: totalMarbles })
      ])

      setOdds({
        singleMarble: singleMarbleOdds.PickWinner,
        twoMarble: twoMarbleOdds.PickWinner,
        threeMarble: threeMarbleOdds.PickWinner
      })
    }

    void fetchOdds()
  }, [totalMarbles, maxPickableMarbles])

  const selectionOptions = getSelectionTypeOptions(gameType, t)

  // `game.id` is the race-configuration id — what the pricing table keys
  // on. Null for a game with no verified pricing, in which case every
  // effective figure below is suppressed rather than guessed at.
  const gameKey = resolveGameKey(isMarbleGame(game) ? game.id : undefined)
  const showEffective = hasLightning(gameKey)

  // Prefer the real backend numbers off the game config over re-deriving
  // odds from a hardcoded margin. The margins in `calculationService` are
  // Marble Plinko's DynamoDB MarginConfig override, so deriving from them
  // reported 91.74% for every game — Rolling Dunes and Snake actually pay
  // 94.34%. The config carries each option's settled multiplier and
  // probability, so base RTP comes straight from those.
  const stats =
    isKnownGameConfig(gameConfig) && gameConfig.markets?.length
      ? getEffectiveBetStats(gameConfig, undefined, gameKey)
      : undefined

  const singleMultiplier = stats?.baseMaxMultiplier ?? odds.singleMarble?.oddsAfterMargin
  const singleBaseRtp =
    stats && stats.rtpProbability > 0
      ? stats.rtpProbability * stats.baseMaxMultiplier * 100
      : calculateOddsRtp(odds.singleMarble)

  // Side-bet combinatorics run over the market's own bettable options.
  // `totalMarbles` comes from race_configuration and reports 8 for Snake
  // even though Snake has 6 outcomes, which would over-quote its
  // forecasts.
  const sideBetOutcomes = stats?.mainMarketOutcomes || totalMarbles

  const columnCount = equalOdds ? 3 : 2

  // Pick Winner effective figures, one per row that actually renders.
  // Chip-placement shows a single row, so this is a single number; the
  // classic 3-row shape collapses to a range OF THOSE ROWS — a range
  // over bets a player can really place, unlike the retired
  // min+1 / max+2.5 band.
  const pickWinnerEffective = (() => {
    if (!showEffective) return null
    const rows = [effectiveRtp(gameKey, singleBaseRtp ?? null, '', 1)]
    if (maxPickableMarbles > 1 && !isChipPlacement) {
      rows.push(effectiveRtp(gameKey, calculateOddsRtp(odds.twoMarble), '', 2))
      rows.push(effectiveRtp(gameKey, calculateOddsRtp(odds.threeMarble), '', 3))
    }
    const known = rows.filter((r): r is number => r !== null)
    if (known.length === 0) return null
    const min = Math.min(...known)
    const max = Math.max(...known)
    return min === max ? `${min.toFixed(2)}%` : `${min.toFixed(2)}-${max.toFixed(2)}%`
  })()

  const sideBetRow = (label: string, betType: SideBetType) => {
    const multiplier = sideBetMultiplier(gameKey, betType, sideBetOutcomes)
    return (
      <tr key={betType}>
        <td>{label}</td>
        {equalOdds && <td>{multiplier > 0 ? `${truncateDecimals(multiplier, 3)}x` : '...'}</td>}
        <td>{formatRTP(sideBetRtp(gameKey, betType))}</td>
      </tr>
    )
  }

  // Each bet type carries its own margin AND its own effective figure —
  // Rolling Dunes pays 90% on Any order but 88% on Exact Order, which
  // Lightning lifts to 92.97% and 90.91%. The retired version copied a
  // single band down from the Pick Winner table, which described
  // neither. Both are named here so the row is unambiguous.
  const sideBetEffectiveLabel = (rows: Array<[string, SideBetType]>) => {
    if (!showEffective) return null
    const parts = rows
      .map(([label, betType]) => {
        const eff = effectiveRtp(gameKey, sideBetRtp(gameKey, betType), betType)
        return eff === null ? null : `${label} ${eff.toFixed(2)}%`
      })
      .filter((p): p is string => p !== null)
    return parts.length > 0 ? parts.join(' · ') : null
  }

  const sideBetTable = (rows: Array<[string, SideBetType]>) => {
    const effectiveLabel = sideBetEffectiveLabel(rows)
    return (
      <div className="rtp-table">
        <table>
          <thead>
            <tr>
              <th>{t('Type')}</th>
              {equalOdds && <th>{t('Max Payout')}</th>}
              <th>{t('RTP')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(([label, betType]) => sideBetRow(label, betType))}
            {effectiveLabel && (
              <tr className="separator-row effective-rtp-row">
                <td colSpan={columnCount}>
                  ⚡ {t('Effective RTP')}*: {effectiveLabel}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    )
  }

  return (
    <>
      <h4 className="yellow">{t('Pick Winner Mode')}</h4>
      <div className="rtp-table">
        <table>
          <thead>
            <tr>
              <th>{t('Selections')}</th>
              {equalOdds && <th>{t('Max Payout')}</th>}
              <th>{t('RTP')}</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{t('1 {{singular}}', { singular: selectionOptions.singular })}</td>
              {equalOdds && (
                <td>{singleMultiplier ? `${truncateDecimals(singleMultiplier, 3)}x` : '...'}</td>
              )}
              <td>{formatRTP(singleBaseRtp)}</td>
            </tr>

            {maxPickableMarbles > 1 && !isChipPlacement && (
              <>
                <tr>
                  <td>
                    {t(`2 {{plural}}`, {
                      plural: selectionOptions.plural
                    })}
                  </td>
                  {equalOdds && (
                    <td>
                      {odds.twoMarble
                        ? `${truncateDecimals(odds.twoMarble.oddsAfterMargin, 3)}x`
                        : '...'}
                    </td>
                  )}
                  <td>{formatRTP(calculateOddsRtp(odds.twoMarble))}</td>
                </tr>
                <tr>
                  <td>
                    {t(`3 {{plural}}`, {
                      plural: selectionOptions.plural
                    })}
                  </td>
                  {equalOdds && (
                    <td>
                      {odds.threeMarble
                        ? `${truncateDecimals(odds.threeMarble.oddsAfterMargin, 3)}x`
                        : '...'}
                    </td>
                  )}
                  <td>{formatRTP(calculateOddsRtp(odds.threeMarble))}</td>
                </tr>
              </>
            )}
            {pickWinnerEffective && (
              <tr className="separator-row effective-rtp-row">
                <td colSpan={columnCount}>
                  ⚡ {t('Effective RTP')}*: {pickWinnerEffective}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      {isExoticBetEnabled(maxPickableMarbles) && gameType !== 'fast_lane' && (
        <>
          <h4 className="tomato">
            {t('Pick Order (2 {{plural}})', { plural: selectionOptions.plural })}
          </h4>
          {sideBetTable([
            [t('Any order'), 'ReverseForecast'],
            [t('Exact Order'), 'StraightForecast']
          ])}

          <h4 className="purple">
            {t('Pick Order (3 {{plural}})', { plural: selectionOptions.plural })}
          </h4>
          {sideBetTable([
            [t('Any order'), 'CombinationTricast'],
            [t('Exact Order'), 'Tricast']
          ])}
        </>
      )}
    </>
  )
}
